import { useState } from "react";
import { updatePassword } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { updateUser } from "@/api/users";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Lock, AlertCircle, CheckCircle2 } from "lucide-react";

export function PasswordResetModal() {
  const { userData, refetchUser } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return "Password must contain at least one special character";
    }
    return null;
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    // Validation
    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      // Update Firebase Auth password
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }

      await updatePassword(currentUser, newPassword);

      // Update Firestore user document
      await updateUser(currentUser.uid, {
        passwordResetRequired: false,
        lastPasswordReset: new Date(),
        updatedAt: new Date(),
      });

      // Refetch user data to update the UI
      await refetchUser();
      
      setSuccess(true);
      setNewPassword("");
      setConfirmPassword("");

      // Auto-close after 2 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (error: any) {
      console.error("Error updating password:", error);
      
      if (error.code === "auth/requires-recent-login") {
        setError("For security reasons, please log out and log back in to change your password");
      } else if (error.code === "auth/weak-password") {
        setError("Password is too weak. Please choose a stronger password");
      } else {
        setError(error.message || "Failed to update password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Only show modal if password reset is required
  if (!userData?.passwordResetRequired) {
    return null;
  }

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-orange-100 rounded-full">
              <Lock className="h-5 w-5 text-orange-600" />
            </div>
            <DialogTitle>Password Update Required</DialogTitle>
          </div>
          <DialogDescription>
            For security reasons, you must set a new password before continuing.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handlePasswordReset} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={loading || success}
              autoComplete="new-password"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={loading || success}
              autoComplete="new-password"
            />
          </div>

          {/* Password requirements */}
          <div className="bg-gray-50 p-3 rounded-lg text-xs space-y-1">
            <p className="font-medium text-gray-700">Password must contain:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-0.5">
              <li>At least 8 characters</li>
              <li>One uppercase letter (A-Z)</li>
              <li>One lowercase letter (a-z)</li>
              <li>One number (0-9)</li>
              <li>One special character (!@#$%^&*...)</li>
            </ul>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="border-green-500 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Password updated successfully!
              </AlertDescription>
            </Alert>
          )}

          <Button 
            type="submit" 
            variant="primary"
            className="w-full" 
            disabled={loading || success}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating Password...
              </>
            ) : success ? (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Password Updated
              </>
            ) : (
              "Update Password"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

